import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatEuro } from "@/lib/formatMoney";
import {
  artikelMatchMethodLabel,
  eingangsrechnungLieferantGpId,
  eingangsrechnungLieferantLabel,
  listEingangsrechnungArtikel,
  stammMatchLabel,
  type EingangsrechnungArtikelLine,
} from "@/lib/invoiceMail";
import { ArtikelDetailDialog } from "@/pages/artikel/ArtikelDetailDialog";
import { ImportToStammDialog } from "@/pages/eingangsrechnungArtikel/ImportToStammDialog";
import { LinkLieferantDialog } from "@/pages/eingangsrechnungArtikel/LinkLieferantDialog";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";

const PAGE_SIZE = 50;

function invoiceLabel(row: EingangsrechnungArtikelLine): string {
  const subject = (row.candidate_subject || "").trim();
  if (subject) return subject;
  return `Mail #${row.candidate_id}`;
}

export function EingangsrechnungArtikelIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const candidateFilter = useMemo(() => {
    const raw = searchParams.get("candidate_id");
    if (!raw) return undefined;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }, [searchParams]);

  const lieferantFilter = useMemo(() => {
    const raw = searchParams.get("lieferant_gp_id");
    if (!raw) return undefined;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }, [searchParams]);

  const [rows, setRows] = useState<EingangsrechnungArtikelLine[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailArtikelId, setDetailArtikelId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkLines, setLinkLines] = useState<EingangsrechnungArtikelLine[]>([]);
  const requestSeqRef = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await listEingangsrechnungArtikel({
        q: search.trim() || undefined,
        candidate_id: candidateFilter,
        lieferant_gp_id: lieferantFilter,
        limit: PAGE_SIZE,
        offset,
      });
      if (seq !== requestSeqRef.current) return;
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      setError(
        err instanceof ApiError
          ? err.message
          : "Lieferanten-Artikel konnten nicht geladen werden.",
      );
      setRows([]);
      setTotal(0);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [search, offset, candidateFilter, lieferantFilter]);

  useEffect(() => {
    setOffset(0);
  }, [search, candidateFilter, lieferantFilter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageIndex = Math.floor(offset / PAGE_SIZE) + 1;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [rows, offset, search, candidateFilter, lieferantFilter]);

  const selectedLines = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const selectedImportLines = useMemo(
    () => selectedLines.filter((r) => !r.stamm_artikel_id),
    [selectedLines],
  );

  function openLinkDialog(lines: EingangsrechnungArtikelLine[]) {
    if (lines.length === 0) return;
    setLinkLines(lines);
    setLinkOpen(true);
  }

  function toggleRow(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage() {
    const pageIds = rows.map((r) => r.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of pageIds) next.delete(id);
      } else {
        for (const id of pageIds) next.add(id);
      }
      return next;
    });
  }

  function clearCandidateFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("candidate_id");
    setSearchParams(next, { replace: true });
  }

  function clearLieferantFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete("lieferant_gp_id");
    setSearchParams(next, { replace: true });
  }

  return (
    <AppShell
      title="Eingangsrechnung-Artikel"
      subtitle="Aus E-Rechnungen erkannte Lieferanten-Positionen (unabhängig vom Ablegen)"
    >
      <div className="space-y-4">
        <Alert variant="info">
          Positionen werden automatisch beim Eintreffen bzw. Scannen einer Mail mit E-Rechnung
          (XML/ZUGFeRD) angelegt — eine Freigabe im Rechnungs-Postfach ist dafür nicht nötig.
          Verknüpfungen zum Stammkatalog werden bei Treffer automatisch gesetzt.
        </Alert>

        {lieferantFilter ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-label-secondary">Filter: Lieferant #{lieferantFilter}</span>
            <Button size="sm" variant="secondary" onClick={clearLieferantFilter}>
              Filter entfernen
            </Button>
            <Link
              to={`/kontakte/lieferanten/${lieferantFilter}`}
              className="text-sm text-accent hover:underline"
            >
              Zum Lieferanten
            </Link>
          </div>
        ) : null}

        {candidateFilter ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-label-secondary">Filter: Mail #{candidateFilter}</span>
            <Button size="sm" variant="secondary" onClick={clearCandidateFilter}>
              Filter entfernen
            </Button>
            <Link
              to="/bestellwesen/rechnungs-postfach"
              className="text-sm text-accent hover:underline"
            >
              Zum Rechnungs-Postfach
            </Link>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[220px] flex-1">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Bezeichnung, EAN, Art.-Nr., Lieferant…"
            />
          </div>
          <Button variant="secondary" onClick={() => void reload()} disabled={loading}>
            Aktualisieren
          </Button>
          <Button
            variant="secondary"
            disabled={selectedLines.length === 0}
            onClick={() => openLinkDialog(selectedLines)}
          >
            Lieferant verknüpfen ({selectedLines.length})
          </Button>
          <Button
            disabled={selectedImportLines.length === 0}
            onClick={() => setImportOpen(true)}
          >
            In Artikelliste übernehmen ({selectedImportLines.length})
          </Button>
          <span className="text-sm text-label-secondary">
            {total.toLocaleString("de-DE")} Position{total === 1 ? "" : "en"}
          </span>
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <Card>
            <p className="px-4 py-8 text-center text-sm text-label-secondary">
              Noch keine E-Rechnungspositionen erkannt. Im{" "}
              <Link to="/bestellwesen/rechnungs-postfach" className="text-accent hover:underline">
                Rechnungs-Postfach
              </Link>{" "}
              „Postfach nachladen“ ausführen oder bestehende Mails erneut analysieren.
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-left text-sm">
                <thead className="border-b border-separator bg-card/80 text-xs uppercase tracking-wide text-label-secondary">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      <input
                        type="checkbox"
                        checked={
                          rows.length > 0 &&
                          rows.every((r) => selectedIds.has(r.id))
                        }
                        disabled={rows.length === 0}
                        onChange={toggleAllOnPage}
                        aria-label="Alle auf dieser Seite auswählen"
                      />
                    </th>
                    <th className="px-3 py-2 font-medium">Lieferant</th>
                    <th className="px-3 py-2 font-medium">Bezeichnung</th>
                    <th className="px-3 py-2 font-medium text-right">Menge</th>
                    <th className="px-3 py-2 font-medium text-right">EP netto</th>
                    <th className="px-3 py-2 font-medium text-right">Zeile netto</th>
                    <th className="px-3 py-2 font-medium">Stamm-Verknüpfung</th>
                    <th className="px-3 py-2 font-medium">Rechnung / Mail</th>
                    <th className="px-3 py-2 font-medium">Erkannt am</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-separator/60 hover:bg-card/40">
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`${row.bezeichnung} auswählen`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-label-secondary">
                        {(() => {
                          const gpId = eingangsrechnungLieferantGpId(row);
                          const label = eingangsrechnungLieferantLabel(row);
                          const rawName = (row.lieferant_name || row.candidate_from_name || "").trim();
                          return (
                            <div className="space-y-1">
                              {gpId && label !== "—" ? (
                                <Link
                                  to={`/kontakte/lieferanten/${gpId}`}
                                  className="text-accent hover:underline"
                                >
                                  {label}
                                </Link>
                              ) : (
                                <span>{label}</span>
                              )}
                              {rawName && gpId && rawName !== label ? (
                                <div className="text-xs text-label-secondary">Rechnung: {rawName}</div>
                              ) : null}
                              <button
                                type="button"
                                className="block text-xs text-accent hover:underline"
                                onClick={() => openLinkDialog([row])}
                              >
                                {gpId ? "Lieferant ändern" : "Lieferant zuordnen"}
                              </button>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-label-primary">{row.bezeichnung}</div>
                        {row.seller_article_nr || row.ean ? (
                          <div className="text-xs text-label-secondary">
                            {row.seller_article_nr ? `Art.-Nr. ${row.seller_article_nr}` : null}
                            {row.seller_article_nr && row.ean ? " · " : null}
                            {row.ean ? `EAN ${row.ean}` : null}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums whitespace-nowrap">
                        {row.menge != null
                          ? `${Number(row.menge).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${row.einheit ? ` ${row.einheit}` : ""}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums whitespace-nowrap">
                        {formatEuro(row.einzelpreis_netto, 2)}
                      </td>
                      <td className="px-3 py-2 text-right align-top tabular-nums whitespace-nowrap">
                        {formatEuro(row.zeilen_netto, 2)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {row.stamm_artikel_id ? (
                          <button
                            type="button"
                            className="text-left text-accent hover:underline"
                            onClick={() => setDetailArtikelId(row.stamm_artikel_id!)}
                          >
                            {stammMatchLabel(row)}
                          </button>
                        ) : (
                          <span className="text-label-secondary">—</span>
                        )}
                        {row.match_method && row.match_method !== "none" ? (
                          <div className="text-xs text-label-secondary">
                            {artikelMatchMethodLabel(row.match_method)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Link
                          to={`/bestellwesen/eingangsrechnung-artikel?candidate_id=${row.candidate_id}`}
                          className="text-accent hover:underline"
                          title={invoiceLabel(row)}
                        >
                          {invoiceLabel(row).length > 48
                            ? `${invoiceLabel(row).slice(0, 48)}…`
                            : invoiceLabel(row)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-label-secondary whitespace-nowrap">
                        {row.created_at ? fmtDateTime(row.created_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pageCount > 1 ? (
              <div className="flex items-center justify-between border-t border-separator px-3 py-2 text-sm">
                <span className="text-label-secondary">
                  Seite {pageIndex} von {pageCount}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={offset <= 0 || loading}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  >
                    Zurück
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={offset + PAGE_SIZE >= total || loading}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  >
                    Weiter
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        )}
      </div>

      <ImportToStammDialog
        open={importOpen}
        lines={selectedImportLines}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setSelectedIds(new Set());
          void reload();
        }}
      />

      <LinkLieferantDialog
        open={linkOpen}
        lines={linkLines}
        onClose={() => setLinkOpen(false)}
        onLinked={() => {
          setSelectedIds(new Set());
          void reload();
        }}
      />

      <ArtikelDetailDialog
        artikelId={detailArtikelId}
        open={detailArtikelId != null}
        onClose={() => setDetailArtikelId(null)}
      />
    </AppShell>
  );
}
